<template>
  <div class="guidance-readiness">
    <!-- Date Selector -->
    <div class="date-selector">
      <md-field>
        <label>Select Date</label>
        <md-input type="date" v-model="selectedDate" @change="fetchReadiness" />
      </md-field>
      <md-button class="md-primary" @click="fetchReadiness">
        <md-icon>refresh</md-icon>
        Refresh
      </md-button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="loading-container">
      <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
    </div>

    <!-- Report -->
    <div v-else-if="report" class="report-container">
      <!-- Overall Score -->
      <div class="overall-score">
        <md-card>
          <md-card-content>
            <div class="score-display">
              <div class="score-circle">
                <svg viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="54" fill="none" stroke="#e0e0e0" stroke-width="12"/>
                  <circle
                      cx="60" cy="60" r="54"
                      fill="none"
                      :stroke="scoreColor"
                      stroke-width="12"
                      :stroke-dasharray="circleLength"
                      :stroke-dashoffset="circleOffset"
                      transform="rotate(-90 60 60)"
                  />
                </svg>
                <div class="score-text">
                  <span class="score-value">{{ report.overall_score }}%</span>
                  <span class="score-label">{{ report.overall_status_label }}</span>
                </div>
              </div>
              <div class="score-stats">
                <div class="stat">
                  <span class="stat-label">Total Required</span>
                  <span class="stat-value">{{ report.total_required }}</span>
                </div>
                <div class="stat">
                  <span class="stat-label">Total Available</span>
                  <span class="stat-value">{{ report.total_available }}</span>
                </div>
                <div class="stat">
                  <span class="stat-label">Deficit</span>
                  <span class="stat-value text-danger">{{ report.total_required - report.total_available }}</span>
                </div>
              </div>
            </div>
          </md-card-content>
        </md-card>
      </div>

      <!-- Summary Cards -->
      <div class="summary-cards">
        <div class="summary-card" v-for="(item, key) in report.summary" :key="key">
          <div v-if="key !=='top_issues'" >
            <span class="summary-label">{{ formatLabel(key) }}</span>
            <span class="summary-value" >{{ item }}</span>
          </div>
        </div>
      </div>

      <!-- Workplace Details -->
      <div v-for="workplace in report.details" :key="workplace.workplace_id" class="workplace-section">
        <md-card>
          <md-card-header>
            <div class="workplace-header">
              <h4>{{ workplace.workplace_name }}</h4>
              <badge :type="getStatusColor(workplace.status)">
                {{ getStatusLabel(workplace.status) }}
              </badge>
              <span class="score-badge">{{ workplace.score }}%</span>
            </div>
          </md-card-header>
          <md-card-content>
            <div v-for="activity in workplace.activities" :key="activity.activity_id" class="activity-section">
              <div class="activity-header">
                <span class="activity-name">{{ activity.activity_name }}</span>
                <badge :type="getStatusColor(activity.status)">
                  {{ getStatusLabel(activity.status) }}
                </badge>
                <span class="score-badge">{{ activity.score }}%</span>
              </div>

              <md-table>
                <md-table-row>
                  <md-table-head>Shift</md-table-head>
                  <md-table-head class="text-right">Required</md-table-head>
                  <md-table-head class="text-right">Available</md-table-head>
                  <md-table-head class="text-right">Deficit</md-table-head>
                  <md-table-head>Status</md-table-head>
                  <md-table-head>Actions</md-table-head>
                </md-table-row>
                <md-table-row v-for="shift in activity.shifts" :key="shift.shift_id">
                  <md-table-cell>{{ shift.shift_name }}</md-table-cell>
                  <md-table-cell class="text-right">{{ shift.required }}</md-table-cell>
                  <md-table-cell class="text-right">{{ shift.available }}</md-table-cell>
                  <md-table-cell class="text-right">
                    <span class="text-danger" v-if="shift.deficit > 0">{{ shift.deficit }}</span>
                    <span class="text-success" v-else>0</span>
                  </md-table-cell>
                  <md-table-cell>
                    <badge :type="getStatusColor(shift.status)">
                      {{ getStatusLabel(shift.status) }}
                    </badge>
                  </md-table-cell>
                  <md-table-cell>
                    <md-button class="md-icon-button md-primary" @click="viewLabourerPool(shift.shift_id)">
                      <md-icon>people</md-icon>
                    </md-button>
                  </md-table-cell>
                </md-table-row>
              </md-table>
            </div>
          </md-card-content>
        </md-card>
      </div>

      <!-- Top Issues -->
      <div class="top-issues" v-if="report.summary.top_issues && report.summary.top_issues.length > 0">
        <md-card>
          <md-card-header>
            <h4>Top Issues</h4>
          </md-card-header>
          <md-card-content>
            <div v-for="issue in report.summary.top_issues" :key="issue.shift" class="issue-item">
              <div class="issue-info">
                <strong>{{ issue.workplace }}</strong> -
                {{ issue.activity }} -
                {{ issue.shift }}
              </div>
              <div class="issue-details">
                <span class="text-danger">
                  Deficit: {{ issue.deficit }}
                  (Required: {{ issue.required }}, Available: {{ issue.available }})
                </span>
                <span class="issue-date">{{ issue.date }}</span>
              </div>
            </div>
          </md-card-content>
        </md-card>
      </div>
    </div>

    <!-- Labourer Pool Modal -->
    <md-dialog :md-active.sync="showPoolModal" class="pool-modal">
      <md-dialog-title>Labourer Pool</md-dialog-title>
      <md-dialog-content>
        <div class="pool-content">
          <div v-if="poolLabourers && poolLabourers.length > 0">
            <p><strong>Total Available:</strong> {{ poolLabourers.length }}</p>
            <md-table>
              <md-table-row>
                <md-table-head>Labourer</md-table-head>
                <md-table-head>Emp Code</md-table-head>
                <md-table-head>Start Date</md-table-head>
                <md-table-head>End Date</md-table-head>
              </md-table-row>
              <md-table-row v-for="labourer in poolLabourers" :key="labourer.labourer_id">
                <md-table-cell>{{ labourer.labourer_name }}</md-table-cell>
                <md-table-cell>{{ labourer.emp_code }}</md-table-cell>
                <md-table-cell>{{ formatDate(labourer.start_date) }}</md-table-cell>
                <md-table-cell>{{ labourer.end_date ? formatDate(labourer.end_date) : 'Ongoing' }}</md-table-cell>
              </md-table-row>
            </md-table>
          </div>
          <div v-else class="empty-state">
            <md-icon>people</md-icon>
            <p>No labourers available in the pool for this shift</p>
          </div>
        </div>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class="md-primary" @click="showPoolModal = false">Close</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>

<script>
import Badge from '@/components/Badge.vue';
import axios from 'axios';

export default {
  name: 'GuidanceReadiness',
  components: {
    Badge
  },
  props: {
    guidanceId: {
      type: [Number, String],
      required: true
    }
  },
  data() {
    return {
      selectedDate: this.getToday(),
      report: null,
      loading: false,
      showPoolModal: false,
      poolLabourers: [],
      circleLength: 2 * Math.PI * 54
    };
  },
  computed: {
    scoreColor() {
      if (!this.report) return '#4caf50';
      const score = this.report.overall_score;
      if (score >= 75) return '#4caf50';
      if (score >= 50) return '#ff9800';
      return '#f44336';
    },
    circleOffset() {
      if (!this.report) return 0;
      const score = this.report.overall_score;
      return this.circleLength - (score / 100) * this.circleLength;
    }
  },
  created() {
    this.fetchReadiness();
  },
  methods: {
    getToday() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },

    async fetchReadiness() {
      this.loading = true;
      const url = process.env.VUE_APP_API_BASE_URL;
      try {
        const response = await axios.get(`${url}/guidances/${this.guidanceId}/readiness`, {
          params: { date: this.selectedDate }
        });
        this.report = response.data.data;
        this.report.overall_status_label = this.getStatusLabel(this.report.overall_status);
      } catch (error) {
        console.error('Error fetching readiness:', error);
        this.$store.dispatch('alerts/error', 'Failed to fetch readiness report');
      } finally {
        this.loading = false;
      }
    },

    async viewLabourerPool(shiftId) {
      try {
        const url = process.env.VUE_APP_API_BASE_URL;
        const response = await axios.get(`${url}/guidances/${this.guidanceId}/labourer-pool`, {
          params: { shift_id: shiftId, date: this.selectedDate }
        });
        this.poolLabourers = response.data.data.labourers || [];
        this.showPoolModal = true;
      } catch (error) {
        console.error('Error fetching labourer pool:', error);
        this.$store.dispatch('alerts/error', 'Failed to fetch labourer pool');
      }
    },

    getStatusColor(status) {
      const colors = {
        'fully_ready': 'success',
        'partially_ready': 'warning',
        'critical': 'danger',
        'unavailable': 'danger',
        'unknown': 'default'
      };
      return colors[status] || 'default';
    },

    getStatusLabel(status) {
      const labels = {
        'fully_ready': 'Fully Ready',
        'partially_ready': 'Partially Ready',
        'critical': 'Critical',
        'unavailable': 'Unavailable',
        'unknown': 'Unknown'
      };
      return labels[status] || status;
    },

    formatLabel(key) {
      return key
          .replace(/_/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase());
    },

    formatDate(date) {
      if (!date) return 'N/A';
      return new Date(date).toLocaleDateString();
    }
  }
};
</script>

<style scoped>
.guidance-readiness {
  padding: 20px;
}

.date-selector {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.date-selector .md-field {
  max-width: 200px;
  margin: 0;
}

.loading-container {
  display: flex;
  justify-content: center;
  padding: 40px;
}

.overall-score {
  margin-bottom: 20px;
}

.score-display {
  display: flex;
  align-items: center;
  gap: 40px;
}

.score-circle {
  position: relative;
  width: 120px;
  height: 120px;
}

.score-circle svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.score-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.score-value {
  display: block;
  font-size: 24px;
  font-weight: 700;
}

.score-label {
  display: block;
  font-size: 12px;
  color: #666;
}

.score-stats {
  display: flex;
  gap: 30px;
}

.stat {
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 12px;
  color: #999;
}

.stat-value {
  display: block;
  font-size: 20px;
  font-weight: 600;
}

.text-danger {
  color: #f44336;
}

.text-success {
  color: #4caf50;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
}

.summary-card {
  background: #f5f5f5;
  padding: 12px 16px;
  border-radius: 8px;
  text-align: center;
}

.summary-label {
  display: block;
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
}

.summary-value {
  display: block;
  font-size: 18px;
  font-weight: 600;
}

.workplace-section {
  margin-bottom: 20px;
}

.workplace-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.workplace-header h4 {
  margin: 0;
  flex: 1;
}

.score-badge {
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: #f5f5f5;
}

.activity-section {
  margin-top: 16px;
}

.activity-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.activity-name {
  font-weight: 500;
  flex: 1;
}

.text-right {
  text-align: right;
}

.issue-item {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.issue-info {
  font-weight: 500;
}

.issue-details {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #666;
  margin-top: 4px;
}

.issue-date {
  color: #999;
  font-size: 12px;
}

.pool-modal .md-dialog {
  max-width: 700px;
  width: 95%;
}

.empty-state {
  text-align: center;
  padding: 40px;
  color: #999;
}

.empty-state .md-icon {
  font-size: 48px;
  width: 48px;
  height: 48px;
}
</style>